import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import {
  Home,
  ShoppingCart,
  Grid3X3,
  Plus,
  RotateCcw,
  Search,
  Calculator,
  Receipt,
  Clock,
  BarChart3,
  LogOut,
  Store,
  User
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_pos-register-pro/artifacts/r59p7j84_PHOTO-2025-11-25-13-33-44.jpg";

const navItems = [
  { path: "/quick-pick", label: "QUICK PICK ITEMS", icon: Grid3X3, color: "bg-blue-600" },
  { path: "/add-item", label: "ADD NEW ITEM", icon: Plus, color: "bg-slate-500" },
  { path: "/products", label: "PRODUCT LIST", icon: Store, color: "bg-blue-600" },
  { path: "/return", label: "ACCEPT RETURN", icon: RotateCcw, color: "bg-green-600" },
  { path: "/search", label: "SEARCH PRODUCT", icon: Search, color: "bg-blue-600" },
  { path: "/register", label: "VIEW REGISTER", icon: Calculator, color: "bg-green-600" },
  { path: "/today-sales", label: "TODAY'S SALES", icon: Receipt, color: "bg-green-600" },
  { path: "/held-sales", label: "ON HOLD SALES", icon: Clock, color: "bg-blue-600" },
];

export default function POSLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const currentDate = new Date().toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50" data-testid="pos-layout">
      {/* Sidebar */}
      <aside className="sidebar" data-testid="sidebar">
        {/* Logo */}
        <div className="sidebar-logo">
          <img 
            src={LOGO_URL} 
            alt="Nimsi's" 
            className="w-20 h-20 object-contain rounded-lg"
          />
          <span className="text-white text-xs font-medium text-center mt-2">
            Nimsi's Stationery
          </span>
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `pos-sidebar-btn ${item.color} ${isActive ? "ring-2 ring-white ring-opacity-50" : ""}`
              }
              data-testid={`nav-${item.path.slice(1)}`}
            >
              <span className="flex items-center gap-2">
                <item.icon className="w-4 h-4" />
                <span className="text-sm">{item.label}</span>
              </span>
            </NavLink>
          ))}
        </nav>

        {/* Logout Button */}
        <div className="p-3 border-t border-slate-700">
          <button
            onClick={handleLogout}
            className="pos-sidebar-btn bg-red-600 flex items-center justify-center gap-2"
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4" />
            <span>LOGOUT</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="header" data-testid="header">
          <div className="header-left">
            <NavLink
              to="/"
              className={({ isActive }) =>
                `px-4 py-2 rounded-md font-semibold text-sm flex items-center gap-2 transition-colors ${
                  isActive ? "bg-red-600 text-white" : "bg-slate-700 text-white hover:bg-slate-600"
                }`
              }
              end
              data-testid="nav-home"
            >
              <Home className="w-4 h-4" />
              Home
            </NavLink>
            <NavLink
              to="/sales"
              className={({ isActive }) =>
                `px-4 py-2 rounded-md font-semibold text-sm flex items-center gap-2 transition-colors ${
                  isActive ? "bg-green-600 text-white" : "bg-slate-700 text-white hover:bg-slate-600"
                }`
              }
              data-testid="nav-add-sales"
            >
              <ShoppingCart className="w-4 h-4" />
              Add Sales
            </NavLink>
            <NavLink
              to="/reports"
              className={({ isActive }) =>
                `px-4 py-2 rounded-md font-semibold text-sm flex items-center gap-2 transition-colors ${
                  isActive ? "bg-blue-600 text-white" : "bg-slate-700 text-white hover:bg-slate-600"
                }`
              }
              data-testid="nav-reports"
            >
              <BarChart3 className="w-4 h-4" />
              Reports
            </NavLink>
          </div>
          <div className="header-right">
            <span className="text-slate-400 text-sm">{currentDate}</span>
            <span className="flex items-center gap-2 text-slate-300 text-sm">
              <Store className="w-4 h-4" />
              Warehouse
            </span>
            <span className="flex items-center gap-2 text-white font-medium">
              <User className="w-4 h-4" />
              {user?.username || "admin"}
            </span>
          </div>
        </header>

        {/* Page Content */}
        <main className="main-content" data-testid="main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
